import React, { useEffect, useState } from 'react';
import { Alert, Backdrop, Box, Button, CircularProgress, Container, Paper, Typography } from '@mui/material';
import Grid2 from '@mui/material/Grid2';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import FormSection from '../../components/FormSection';
import MultiSelectPicklist from '../../components/MultiSelectPicklist';
import CountyAreasFields from '../../components/CountyAreasFields';
import { useAuth } from '../../context/AuthContext';
import { AvailabilityFormData, EMPTY_FORM, fromApi, toApi, validateAvailability } from './availabilityOptions';

/** Availability update page opened from the texted link; laid out like the caregiver form. */
const AvailabilityUpdate: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { authFetch, logout, user } = useAuth();

  const [formData, setFormData] = useState<AvailabilityFormData>(EMPTY_FORM);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [firstName, setFirstName] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Same recovery as the caregiver form: a stale caregiver link sends the user back to verify their phone.
  const handleResolutionFailure = async (response: Response): Promise<boolean> => {
    if (response.status !== 409) return false;
    const body = await response.clone().json().catch(() => null);
    if (body?.code === 'CAREGIVER_FORM_IN_PROGRESS') {
      navigate('/caregiverform', { replace: true });
      return true;
    }
    if (body?.code !== 'CAREGIVER_REGISTRATION_REQUIRED' && body?.code !== 'CAREGIVER_LINK_CONFLICT') return false;

    const mobile = user?.username?.replace(/\D/g, '') || '';
    await logout();
    const params = new URLSearchParams({
      [body.code === 'CAREGIVER_REGISTRATION_REQUIRED' ? 'registrationRequired' : 'caregiverLinkConflict']: 'true',
      next: 'availability',
    });
    if (mobile) params.set('mobile', mobile);
    navigate(`/start?${params.toString()}`, { replace: true });
    return true;
  };

  useEffect(() => {
    const load = async () => {
      try {
        const response = await authFetch('/api/caregiver/availability');
        if (!response.ok) {
          if (!(await handleResolutionFailure(response))) setError(t('availability.loadFailed'));
          return;
        }
        const data = await response.json();
        setFormData(fromApi(data.availability ?? {}));
        setFirstName(data.firstName || null);
      } catch {
        setError(t('availability.loadFailed'));
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, []);

  const clearFieldError = (...names: string[]) =>
    setFieldErrors((prev) => {
      const next = { ...prev };
      for (const name of names) delete next[name];
      return next;
    });

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setFormData((prev) => ({ ...prev, [name]: checked }));
    clearFieldError('countySelection');
  };

  const handleMultiSelectChange = (e: { target: { name: string; value: string[] } }) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value.join(';') }));
    clearFieldError(name);
  };

  const handleSubmit = async () => {
    setError(null);
    const errors = validateAvailability(formData);
    setFieldErrors(Object.fromEntries(Object.entries(errors).map(([field, key]) => [field, t(key)])));
    if (Object.keys(errors).length > 0) return;

    setIsSaving(true);
    try {
      const response = await authFetch('/api/caregiver/availability', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(toApi(formData)),
      });
      if (!response.ok) {
        if (!(await handleResolutionFailure(response))) setError(t('availability.saveFailed'));
        return;
      }
      const data = await response.json();
      setFormData(fromApi(data.availability ?? {}));
      setIsSaved(true);
      window.scrollTo({ top: 0 });
    } catch {
      setError(t('availability.saveFailed'));
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mb: 4 }}>
      <Paper elevation={3} sx={{ my: 2, p: 2, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Box sx={{ width: '100%', mt: '1rem', p: '0.25rem' }}>
          {isSaved ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <CheckCircleIcon color="success" sx={{ fontSize: 72 }} />
              <Typography variant="h5" sx={{ mt: 1 }}>
                {t('availability.thankYou')}
              </Typography>
              <Typography color="text.secondary" sx={{ mt: 1 }}>
                {t('availability.thankYouDetail')}
              </Typography>
              <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
                <Button variant="outlined" onClick={() => { setIsSaved(false); setFieldErrors({}); }}>
                  {t('availability.editAgain')}
                </Button>
                <Button variant="contained" onClick={() => navigate('/caregivers')}>
                  {t('availability.backHome')}
                </Button>
              </Box>
            </Box>
          ) : (
            <>
              <Box sx={{ mx: 1, mb: 2 }}>
                <Typography variant="h5" gutterBottom>
                  {t('availability.title')}
                </Typography>
                <Typography color="text.secondary">
                  {firstName ? t('availability.introName', { name: firstName }) : t('availability.intro')}
                </Typography>
              </Box>

              <Box sx={{ m: { xs: 0.5, sm: 1 }, ml: { xs: 1 }, width: { xs: '100%', sm: 'auto' } }}>
                <FormSection title={t('form.availability', 'Availability')} icon={<EventAvailableIcon />}>
                  <Grid2 container spacing={2}>
                    <Grid2 size={{ xs: 12, sm: 6 }}>
                      <MultiSelectPicklist
                        fieldName="availabilityPreferences"
                        value={formData.availabilityPreferences}
                        onChange={handleMultiSelectChange}
                        label={t('form.availabilityPreferencesLabel', 'Available Shifts')}
                        objectName="caregiver"
                        required
                        error={!!fieldErrors.availabilityPreferences}
                        helperText={fieldErrors.availabilityPreferences}
                      />
                    </Grid2>
                    <Grid2 size={{ xs: 12, sm: 6 }}>
                      <MultiSelectPicklist
                        fieldName="availableDays"
                        value={formData.availableDays}
                        onChange={handleMultiSelectChange}
                        label={t('form.availableDays', 'Available Days')}
                        objectName="caregiver"
                        required
                        error={!!fieldErrors.availableDays}
                        helperText={fieldErrors.availableDays}
                      />
                    </Grid2>
                    <CountyAreasFields
                      values={formData}
                      fieldErrors={fieldErrors}
                      handleCheckboxChange={handleCheckboxChange}
                      handleMultiSelectChange={handleMultiSelectChange}
                    />
                  </Grid2>
                </FormSection>
              </Box>

              {error && <Alert severity="error" sx={{ mx: 1, mt: 2 }}>{error}</Alert>}

              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2, px: 1 }}>
                <Button variant="contained" onClick={handleSubmit} disabled={isSaving}>
                  {isSaving ? t('form.saving', 'Saving...') : t('form.submit', 'Submit')}
                </Button>
              </Box>
            </>
          )}
        </Box>
      </Paper>

      <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={isSaving}>
        <CircularProgress color="inherit" />
      </Backdrop>
    </Container>
  );
};

export default AvailabilityUpdate;
